<script setup lang="ts">
import { Head, Link, router } from "@inertiajs/vue3"

import BatchListCard from "@/components/batches/BatchListCard.vue"
import NarrowPage from "@/components/NarrowPage.vue"
import PaginationNav from "@/components/PaginationNav.vue"
import { useNavbar } from "@/composables/useNavbar"
import { ICON } from "@/lib/icons"
import { batchesPath, capturePath } from "@/routes"
import type { CaptureBatchSummary } from "@/types/capture"
import type { PaginationMeta } from "@/types/models"

type BatchFilter = "open" | "completed" | "archived"

const props = defineProps<{
  batches: CaptureBatchSummary[]
  status: BatchFilter
  pagination: PaginationMeta
}>()

const filters: { label: string; value: BatchFilter }[] = [
  { label: "Open", value: "open" },
  { label: "Completed", value: "completed" },
  { label: "Archived", value: "archived" },
]

const { title } = useNavbar({ title: "Batches" })

function selectFilter(status: BatchFilter) {
  if (status === props.status) return

  router.visit(batchesPath(), {
    data: { status },
    preserveState: true,
  })
}
</script>

<template>
  <Head :title="title" />

  <NarrowPage>
    <div class="space-y-1">
      <h1 class="text-highlighted text-xl font-semibold">Batches</h1>
      <p class="text-muted text-sm">Review photos captured in bulk and turn them into inventory.</p>
    </div>

    <div class="bg-elevated grid grid-cols-3 rounded-lg p-1" role="group" aria-label="Batch status">
      <button
        v-for="filter in filters"
        :key="filter.value"
        type="button"
        :data-filter="filter.value"
        :aria-pressed="status === filter.value"
        class="min-h-11 rounded-md px-3 text-sm font-medium transition"
        :class="
          status === filter.value
            ? 'bg-default text-highlighted shadow-sm'
            : 'text-muted hover:text-highlighted'
        "
        @click="selectFilter(filter.value)"
      >
        {{ filter.label }}
      </button>
    </div>

    <div v-if="batches.length" class="flex flex-col gap-3">
      <BatchListCard v-for="batch in batches" :key="batch.id" :batch="batch" />
      <PaginationNav :pagination="pagination" />
    </div>

    <div
      v-else
      class="border-default flex flex-col items-center gap-3 rounded-lg border p-8 text-center"
    >
      <UIcon :name="ICON.batches" class="text-muted size-8" />
      <div>
        <h2 class="text-highlighted font-semibold">No {{ status }} batches</h2>
        <p class="text-muted mt-1 text-sm">
          {{
            status === "open"
              ? "Start a bulk capture to collect photos for review."
              : `There are no ${status} capture batches.`
          }}
        </p>
      </div>
      <Link
        v-if="status === 'open'"
        :href="capturePath()"
        data-testid="start-bulk-capture"
        class="bg-primary text-inverted rounded-md px-4 py-2 text-sm font-medium"
      >
        Start bulk capture
      </Link>
    </div>
  </NarrowPage>
</template>
